import { BalanceData, Card, CardKind, LevelSim, OfferKind } from "@highmarch/domain";
import { icon } from "./iconsKit";
import { cardClass, cardFace, flyersInNextWave } from "./cardArt";
import { motionOk } from "./kit";
import type { StatChange, UpgradeSelection } from "./upgradePreview";
import { guide } from "./guide/guide";
import { openingRoadHint, pacingHint } from "./cardHints";
import { BranchPicker } from "./branchPicker";
import { SpecialSlot, handCards, setDisabled, specialPlacing } from "./ascendTray";
import { setText } from "./hudPills";
import { offerIsRare } from "../rare/rareApi";
import { rallyIsWyrm } from "../rare/wyrmApi";

/** Pick feedback length: the chosen card swells and the others drop before the tray morphs into the placement bar. */
const PICK_MS = 180;
/** Reroll: the old hand flips edge-on (rotateY 90°) in this long, then the new cards flip in from edge-on. */
const FLIP_OUT_MS = 140;
/** M36: the rare rally deals this many offers back to back (contract §3). */
const RARE_RALLY_OFFERS = 3;
/** Towers unlocked at this level are the starting set: only later ones get the guide's "new tower" line. */
const STARTING_UNLOCK_LEVEL = 1;

export interface HandHandlers {
  pick(i: number): void; skip(): void; reroll(): void; rotate(): void; place(): void; back(): void; undo?(): void;
  /** Tier 4: the branch picker's choice for the picked Ascend card (docs/TOWERS_V2.md). */
  chooseBranch?(slot: number): void;
}

/** An Upgrade card in hand: how many towers it can raise, and the one chosen (with its before → after), if any. */
export interface UpgradeInHand { readonly targets: number; readonly selected: UpgradeSelection | null }

/**
 * The card tray (P1.10.5): parchment tray with a ribbon title, three cards, SKIP / REROLL; placement mode swaps in
 * BACK / ROTATE / PLACE — for an Upgrade card BACK / UPGRADE, with the chosen tower's before → after stats.
 */
export class CardHandView {
  readonly root: HTMLDivElement;
  private readonly ribbon: HTMLElement;
  /** The road's length so far: it never finishes (DESIGN §4.3), so the tray counts what is laid, not what is left. */
  private readonly roadLength: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly cards: HTMLButtonElement[] = [];
  /** Tier 4: the gold special card (Ascend or a skill) that rides a hand as an extra, above the tray (ascendTray.ts). */
  private readonly special: SpecialSlot;
  private readonly picker: BranchPicker;
  private readonly actions: HTMLDivElement;
  private readonly skip: HTMLButtonElement;
  private readonly reroll: HTMLButtonElement;
  /** UNDO the last card (D17), first in the row while the opening draft has one to take back. */
  private readonly undo: HTMLButtonElement;
  private readonly placement: HTMLDivElement;
  private readonly placeTitle: HTMLElement;
  private readonly placeHint: HTMLElement;
  /** The Upgrade card's before → after strip for the chosen tower ("DMG 6 → 9 · RANGE 2.0 → 2.25"). */
  private readonly strip: HTMLElement;
  private stripKey = "";
  private readonly rotateBtn: HTMLButtonElement;
  private readonly placeBtn: HTMLButtonElement;
  /** One dot per road option under ROTATE, the shown one lit: how many fits there are and which is up. */
  private readonly dots: HTMLElement;
  private dotsKey = "";
  private cardKey = "";
  /** A pick or reroll is animating; taps are ignored until it hands over to the sim. */
  private busy = false;
  /** The next new hand flips in (after a reroll) instead of being dealt. */
  private flipNext = false;
  private wasPlacing = false;
  /** M36 rare rally: the rare offers seen in a row (offer id → its number, 1–3); cleared by the first plain offer. */
  private readonly rally = new Map<number, number>();
  private rallyShown = "";

  constructor(parent: HTMLElement, private readonly balance: BalanceData, h: HandHandlers) {
    this.root = document.createElement("div");
    this.root.id = "hand";
    this.root.className = "tray";
    this.root.innerHTML = `<div class="ribbon disp"></div><div class="line"><span class="roadlen">Road <b></b></span><span class="hint"></span></div><div class="cards"></div><div class="actions row two"></div><div class="placement hidden"><div class="line"><b class="ptitle"></b><span class="phint"></span></div><div class="upstrip hidden"></div><div class="actions row three"></div></div>`;
    this.ribbon = this.root.querySelector(".ribbon")!;
    this.roadLength = this.root.querySelector(".roadlen")!;
    this.hint = this.root.querySelector(".hint")!;
    const cards = this.root.querySelector(".cards")!;
    // One slot per card the sim can deal (`offerSize`), so a hand is never cut short on screen.
    for (let i = 0; i < balance.cards.offerSize; i++) {
      const b = document.createElement("button");
      b.className = "card";
      b.style.setProperty("--i", String(i));
      b.onclick = () => this.pickAnimated(i, () => h.pick(i));
      // The deal/flip classes carry will-change for their run only; drop them when the animation ends (≤ 3 layers).
      b.addEventListener("animationend", () => b.classList.remove("deal", "flipin"));
      cards.append(b);
      this.cards.push(b);
    }
    this.special = new SpecialSlot(this.root, (i) => this.pickAnimated(i, () => h.pick(i)));
    this.picker = new BranchPicker(parent, balance, (slot) => h.chooseBranch?.(slot), h.back);
    this.actions = this.root.querySelector(".actions.two")!;
    this.skip = document.createElement("button"); this.skip.className = "btn dark disp"; this.skip.dataset.guide = "skip"; this.skip.textContent = "SKIP"; this.skip.onclick = h.skip;
    this.reroll = document.createElement("button"); this.reroll.className = "btn blue disp"; this.reroll.dataset.guide = "reroll"; this.reroll.innerHTML = `${icon("fast", 18)} <span class="lbl">REROLL</span>`; this.reroll.onclick = () => this.rerollAnimated(h.reroll);
    this.undo = document.createElement("button"); this.undo.className = "btn dark disp undo hidden"; this.undo.dataset.guide = "undo"; this.undo.setAttribute("aria-label", "Undo the last card");
    this.undo.innerHTML = `${icon("back", 18)} UNDO`; this.undo.onclick = () => { if (!this.busy) h.undo?.(); };
    this.actions.append(this.undo, this.skip, this.reroll);
    this.placement = this.root.querySelector(".placement")!;
    this.placeTitle = this.placement.querySelector(".ptitle")!;
    this.placeHint = this.placement.querySelector(".phint")!;
    this.strip = this.placement.querySelector(".upstrip")!;
    const pa = this.placement.querySelector(".actions")!;
    const back = document.createElement("button"); back.className = "btn dark disp"; back.innerHTML = `${icon("back", 18)} BACK`; back.onclick = h.back;
    this.rotateBtn = document.createElement("button"); this.rotateBtn.className = "btn blue disp rotate"; this.rotateBtn.innerHTML = `<span class="lbl">ROTATE</span><span class="dots"></span>`; this.rotateBtn.onclick = h.rotate;
    this.dots = this.rotateBtn.querySelector(".dots")!;
    this.placeBtn = document.createElement("button"); this.placeBtn.className = "btn gold disp accent"; this.placeBtn.textContent = "PLACE"; this.placeBtn.onclick = h.place;
    pa.append(back, this.rotateBtn, this.placeBtn);
    parent.append(this.root);
  }

  /**
   * `ghostIndex` is the road option shown; `towerSpotChosen` whether a tower card has its spot; `towerCells` how many
   * legal tower cells there are (−1 when not counted), so a card with nowhere to go can say so; `upgrade` the Upgrade
   * card's targets and chosen tower, when one is in hand.
   */
  refresh(sim: LevelSim, ghostIndex = 0, towerSpotChosen = false, towerCells = -1, upgrade: UpgradeInHand | null = null, undoable = false, covers = -1, moving: string | null = null): void {
    const offer = sim.currentOffer;
    this.root.classList.toggle("hidden", offer === null);
    const rally = this.rallyOf(sim);
    this.picker.show(sim, offer !== null && offer.hasPick && offer.pickedCard.kind === CardKind.Ascend ? offer.pickedCard.index : -1);
    if (offer === null) { this.cardKey = ""; this.busy = false; this.special.clear(); return; }
    const placing = offer.hasPick;
    const backFromPlacing = this.wasPlacing && !placing;
    this.wasPlacing = placing;
    if (placing) this.busy = false;
    this.placement.classList.toggle("hidden", !placing);
    this.root.querySelector(".cards")!.classList.toggle("hidden", placing);
    this.actions.classList.toggle("hidden", placing);
    this.root.querySelector(".line:not(.placement .line)")!.classList.toggle("hidden", placing);
    this.special.toggle(!placing);
    if (placing) {
      const card = offer.pickedCard;
      if (card.kind === CardKind.Ascend || card.kind === CardKind.Skill) {
        specialPlacing({ ribbon: this.ribbon, title: this.placeTitle, hint: this.placeHint, place: this.placeBtn, rotate: this.rotateBtn }, this.balance, card, (el, t) => this.setHint(el, t));
        this.setStrip(null);
        return;
      }
      setText(this.ribbon, card.kind === CardKind.Path ? "Lay the road" : card.kind === CardKind.Upgrade ? "Upgrade" : card.kind === CardKind.Move ? "Move a tower" : "Build");
      setText(this.placeBtn, card.kind === CardKind.Upgrade ? "UPGRADE" : card.kind === CardKind.Move ? "MOVE" : "PLACE");
      this.setStrip(card.kind === CardKind.Upgrade ? upgrade?.selected?.changes ?? null : null);
      if (card.kind === CardKind.Path) {
        const s = this.balance.cards.shapes[card.index];
        const n = offer.ghosts.length;
        setText(this.placeTitle, `${s.name} · ${s.tiles.length} tiles`);
        this.setHint(this.placeHint, n === 0 ? "No room here — go BACK" : n > 1 ? `Fit ${ghostIndex + 1} of ${n} · ROTATE` : "PLACE it");
        this.rotateBtn.classList.remove("hidden");
        this.setDots(n, ghostIndex);
        setDisabled(this.placeBtn, n === 0);
        this.placeBtn.classList.toggle("ready", n > 0);
        setDisabled(this.rotateBtn, n <= 1);
        this.placeBtn.classList.remove("hidden");
      } else if (card.kind === CardKind.Upgrade) {
        // Choose, see, confirm: a tap chooses a glowing tower and the strip shows what the card will do to it; UPGRADE
        // (or a second tap on it) spends the card. It used to spend it on the first tap, unseen.
        const chosen = upgrade?.selected ?? null;
        this.rotateBtn.classList.add("hidden");           // an upgrade has nothing to rotate
        setText(this.placeTitle, chosen ? chosen.title : "Upgrade card");
        this.setHint(this.placeHint, upgrade?.targets === 0 ? "Every tower is at its top tier — go BACK" : chosen ? "Tap it again or UPGRADE" : "Tap a glowing tower or the fortress");
        this.placeBtn.classList.remove("hidden");
        setDisabled(this.placeBtn, chosen === null);
        this.placeBtn.classList.toggle("ready", chosen !== null);
      } else if (card.kind === CardKind.Move) {
        // M55: choose the tower, then its new square, then confirm (MOVE or a second tap on the square).
        const ready = moving !== null && towerSpotChosen;
        this.rotateBtn.classList.add("hidden");
        setText(this.placeTitle, moving ? `Move the ${moving}` : "Move a tower");
        this.setHint(this.placeHint, moving === null ? "Tap a glowing tower to move it" : towerCells === 0 ? "No free ground by the road — go BACK"
          : towerSpotChosen ? "Tap again or MOVE · keeps its tier" : "Tap a highlighted square for it");
        this.placeBtn.classList.remove("hidden");
        setDisabled(this.placeBtn, !ready);
        this.placeBtn.classList.toggle("ready", ready);
      } else {
        setText(this.placeTitle, this.balance.towers[card.index].name);
        // PLACE stays disabled until a spot is chosen, and the hint says what enables it; once chosen PLACE lights up.
        const cover = covers >= 0 ? `Covers ${covers} road tile${covers === 1 ? "" : "s"} · ` : "";
        this.setHint(this.placeHint, towerCells === 0 ? "No free ground by the road — go BACK" : towerSpotChosen ? `${cover}tap again or PLACE` : "Tap a glowing square");
        this.rotateBtn.classList.add("hidden");           // a tower has nothing to rotate
        this.placeBtn.classList.remove("hidden");
        setDisabled(this.placeBtn, !towerSpotChosen);
        this.placeBtn.classList.toggle("ready", towerSpotChosen);
      }
      return;
    }
    if (rally) this.showRally(rally);
    else setText(this.ribbon, offer.kind === OfferKind.OpeningPath ? "Lay the road" : offer.kind === OfferKind.OpeningTower ? "Starting towers" : "Pick a card");
    setText(this.roadLength.querySelector("b"), `${sim.road.designedTileCount} tiles`);
    this.setHint(this.hint, rally ? "Towers & upgrades only · no roads" : offer.kind === OfferKind.OpeningPath ? openingRoadHint(offer.cards) : offer.kind === OfferKind.OpeningTower ? "Pick a tower" : pacingHint(sim, offer.cards));
    const key = `${offer.id}|${offer.cards.map((c) => `${c.kind}:${c.index}`).join(",")}|${sim.road.designedTileCount}`;
    if (key !== this.cardKey) {
      this.cardKey = key;
      const anim = motionOk() ? (this.flipNext ? " flipin" : " deal") : "";
      this.flipNext = false;
      this.busy = false;
      const flyers = offer.cards.some((c) => c.kind === CardKind.Tower) && flyersInNextWave(sim);
      const real = handCards(offer.cards);
      this.special.deal(this.balance, offer.cards, anim);
      for (let i = 0; i < this.cards.length; i++) {
        const b = this.cards[i];
        const present = i < real;
        b.classList.toggle("hidden", !present);
        if (!present) continue;
        const card = offer.cards[i];
        b.className = `card ${cardClass(this.balance, card)}${anim}`;
        b.innerHTML = cardFace(this.balance, card, (shape) => sim.road.countSegmentsOfShape(shape), sim.pickDelay(card.kind), flyers);
      }
      this.signalNewTowers(offer.cards);
    } else if (backFromPlacing) {
      // BACK from the placement bar: the same hand is dealt again rather than blinking back in.
      this.busy = false;
      for (const b of this.cards) { b.classList.remove("picked", "dropped", "flipout", "deal"); if (motionOk()) { void b.offsetWidth; b.classList.add("deal"); } }
    }
    // An in-run skip pays a little toward the next offer (DESIGN §4.8.7); the opening draft's skip pays nothing.
    const skipBonus = sim.skipReward;
    setText(this.skip, skipBonus > 0 ? `SKIP · +${skipBonus} pts` : "SKIP");
    setText(this.reroll.querySelector(".lbl"), `REROLL · ${sim.rerollCharges}`);
    setDisabled(this.reroll, sim.rerollCharges <= 0);
    if (this.undo.classList.contains("hidden") === undoable) { this.undo.classList.toggle("hidden", !undoable); this.actions.classList.toggle("undoable", undoable); }
  }

  /**
   * M36: the rare rally's place for the open offer — "1/3" for the first of the three back-to-back rare offers — or null
   * for a plain offer. The tray turns gold while one is open (rare.css `.tray.rare`).
   */
  private rallyOf(sim: LevelSim): number | null {
    const offer = sim.currentOffer;
    const rare = offer !== null && offerIsRare(sim);
    this.root.classList.toggle("rare", rare);
    const wyrm = rare && rallyIsWyrm(sim);                 // M40: the Starfall Wyrm's rally, in prism
    if (this.root.classList.contains("wyrm") !== wyrm) { this.root.classList.toggle("wyrm", wyrm); this.rallyShown = ""; }
    if (!offer) return null;
    if (!rare) { if (this.rally.size > 0 && !this.rally.has(offer.id)) this.rally.clear(); return null; }
    let n = this.rally.get(offer.id);
    if (n === undefined) { n = Math.min(RARE_RALLY_OFFERS, this.rally.size + 1); this.rally.set(offer.id, n); }
    return n;
  }

  private showRally(n: number): void {
    const key = `${n}`;
    if (this.rallyShown === key && this.ribbon.querySelector(".rr-n")) return;
    this.rallyShown = key;
    this.ribbon.innerHTML = `${this.root.classList.contains("wyrm") ? "STARFALL RALLY" : "RARE RALLY"}<span class="rr-n">${n}/${RARE_RALLY_OFFERS}</span>`;
    if (motionOk() && typeof this.ribbon.animate === "function") this.ribbon.animate([{ transform: "scale(1.25)" }, { transform: "none" }], { duration: 260, easing: "cubic-bezier(.2,1.3,.4,1)" });
  }

  /**
   * Pick feedback (F3): the chosen card swells to 1.12 with an inner glow while the other two drop 20 px and fade, over
   * 180 ms, and only then does the sim hear about it (which morphs the tray into the placement bar).
   */
  private pickAnimated(i: number, pick: () => void): void {
    if (this.busy) return;
    if (!motionOk()) { pick(); return; }
    this.busy = true;
    this.cards.forEach((b, j) => { b.classList.remove("deal", "flipin"); b.classList.add(j === i ? "picked" : "dropped"); });
    window.setTimeout(() => {
      const before = this.cardKey;
      pick();
      // If the pick was refused (not enough points…) the tray stays: put the hand back once the next refresh has run.
      window.setTimeout(() => {
        if (!this.wasPlacing && this.cardKey === before) { this.busy = false; for (const b of this.cards) b.classList.remove("picked", "dropped"); }
      }, 60);
    }, PICK_MS);
  }

  /** Reroll (F3): the old cards flip edge-on, then the sim deals the new hand, which flips in (`.flipin`). */
  private rerollAnimated(reroll: () => void): void {
    if (this.busy || this.reroll.disabled) return;
    if (!motionOk()) { reroll(); return; }
    this.busy = true;
    for (const b of this.cards) { b.classList.remove("deal", "flipin"); b.classList.add("flipout"); }
    window.setTimeout(() => {
      this.flipNext = true;
      const before = this.cardKey;
      reroll();
      // Same hand back (the sim refused, or a refresh hasn't run yet): un-flip so the cards never stay edge-on.
      window.setTimeout(() => {
        if (this.cardKey === before) { this.flipNext = false; this.busy = false; for (const b of this.cards) b.classList.remove("flipout"); }
      }, 60);
    }, FLIP_OUT_MS);
  }

  /**
   * Says why a tap or a pick did nothing, in the tray's hint line (the one in view: the placement bar's or the
   * hand's), flashed for `ms` over the usual hint. A banner at the top of the screen was too far from the thumb.
   */
  nudge(text: string, ms: number): void {
    this.nudgeText = text;
    this.nudgeUntil = performance.now() + ms;
    for (const el of [this.placeHint, this.hint]) {
      el.classList.remove("nudge");
      void el.offsetWidth;                                  // restart the flash if it is already running
      el.classList.add("nudge");
    }
    window.setTimeout(() => { if (performance.now() >= this.nudgeUntil) for (const el of [this.placeHint, this.hint]) el.classList.remove("nudge"); }, ms);
    this.setHint(this.placeHint, this.placeHint.textContent ?? "");
    this.setHint(this.hint, this.hint.textContent ?? "");
  }
  private nudgeText = "";
  private nudgeUntil = 0;

  /** A hint line's text, unless a nudge is showing there. */
  private setHint(el: HTMLElement, text: string): void { setText(el, performance.now() < this.nudgeUntil ? this.nudgeText : text); }

  /**
   * One voice (DESIGN §6.2): while a walkthrough bubble speaks, the tray's own hint lines keep quiet rather than say
   * the same thing in other words. Hidden by visibility, so the tray keeps its height.
   */
  setGuideQuiet(on: boolean): void {
    if (on === this.guideQuiet) return;
    this.guideQuiet = on;
    for (const el of [this.hint, this.placeHint]) el.style.visibility = on ? "hidden" : "";
  }
  private guideQuiet = false;

  /** A hand with a tower card that unlocks after level 1: the guide may introduce that tower (`tower:<id>`). */
  private signalNewTowers(cards: readonly Card[]): void {
    const g = guide();
    if (g === null) return;
    for (const c of cards) {
      const a = c.kind === CardKind.Tower ? this.balance.towers[c.index] : null;
      if (a !== null && a.unlockLevel > STARTING_UNLOCK_LEVEL) g.signal(`tower:${a.id}`);
    }
  }

  /** The before → after strip: one "LABEL now → next" per stat the upgrade changes; hidden with nothing chosen. */
  private setStrip(changes: readonly StatChange[] | null): void {
    const key = changes === null ? "" : changes.map((c) => `${c.label}:${c.now}:${c.next}`).join("|");
    if (key === this.stripKey) return;
    this.stripKey = key;
    this.strip.classList.toggle("hidden", changes === null);
    this.strip.innerHTML = changes === null ? "" : changes.map((c) => `<span><i>${c.label}</i> ${c.now}<em>→</em><b>${c.next}</b></span>`).join("");
  }

  /** Rebuilds the option dots only when the count or the lit one changes. */
  private setDots(n: number, lit: number): void {
    const key = `${n}|${lit}`;
    if (key === this.dotsKey) return;
    this.dotsKey = key;
    this.dots.innerHTML = n > 1 ? Array.from({ length: n }, (_, i) => `<i class="${i === lit ? "on" : ""}"></i>`).join("") : "";
  }

  dispose(): void { this.picker.dispose(); this.root.remove(); }
}
